import { FC } from "react";

import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { useTranslate } from "@/common/hooks/useTranslate";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import Image from "@/components/Image/Image";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormHoverBox } from "@/forms-design-system/FormHoverBox";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { ExternalLink } from "@/icons/outlined";

const LOGO_SIZE = 40;

export type ArchivedCampaignBannerViewProps = {
  organizationName: string;
  logoUrl: string | null;
  publishedOrgPageUrl: string | null;
  onOrgPageOpen?: () => void;
};

/**
 * Presentational banner for archived campaigns: links to org page with optional logo and CTA.
 * Decoupled from FormV2 context; data and callback are passed via props.
 */
export const ArchivedCampaignBannerView: FC<ArchivedCampaignBannerViewProps> = ({
  organizationName,
  logoUrl,
  publishedOrgPageUrl,
  onOrgPageOpen,
}) => {
  const { t } = useTranslate();
  const theme = useTheme();

  const logoPreview =
    logoUrl !== null && logoUrl !== undefined ? (
      <Box
        sx={{
          width: LOGO_SIZE,
          height: LOGO_SIZE,
          borderRadius: theme.radius(0.5),
          overflow: "hidden",
          flexShrink: 0,
          backgroundColor: "white",
        }}
      >
        <Image
          src={logoUrl}
          alt={organizationName}
          width={LOGO_SIZE}
          height={LOGO_SIZE}
          nextImage
          sx={{ objectFit: "contain", width: "100%", height: "100%" }}
        />
      </Box>
    ) : undefined;

  if (!publishedOrgPageUrl) {
    return null;
  }

  return (
    <FormHoverBox
      href={publishedOrgPageUrl}
      onClick={onOrgPageOpen}
      target="_blank"
      slots={{
        preview: logoPreview,
        header: (
          <FormTypography variant="subtitle2" color="text.moderate">
            {t("donationForm", "archivedFormBanner.title", { organizationName })}
          </FormTypography>
        ),
        actions: (
          <FormButton
            variant="outline"
            size="small"
            endIcon={
              <FormIcon size="small">
                <ExternalLink />
              </FormIcon>
            }
          >
            {t("donationForm", "archivedFormBanner.viewActiveCampaigns")}
          </FormButton>
        ),
      }}
    >
      <FormTypography variant="body2" color="text.moderate">
        {t("donationForm", "archivedFormBanner.description")}
      </FormTypography>
    </FormHoverBox>
  );
};
